import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, error } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, OpenHours, Participant, ParticipantAvailability, SpecificTimeAvailability, ThemeConfig } from '@nylas/core';
import { DEFAULT_OPEN_HOURS } from '@/common/constants';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * The `nylas-participants-custom-availability` component is a form input for setting custom availability for participants.
 *
 * @part npca - The participants custom availability container
 * @part npca__header - The header of the participants custom availability
 * @part npca__validation-error - The validation error message container
 * @part npca__content - The content of the participants custom availability
 * @part npca__participant-container - The participant container
 * @part npca__participant-title - The title of the participant
 * @part npca__participant-toggle--container - The toggle container for the participant
 * @part npca__toggle-label - The label of the toggle
 * @part npca_toggle-input - The input of the toggle
 * @part npca_toggle-slider - The slider of the toggle
 */
@Component({
  tag: 'nylas-participants-custom-availability',
  styleUrl: 'nylas-participants-custom-availability.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasParticipantsCustomAvailability {
  @Element() host!: HTMLNylasParticipantsCustomAvailabilityElement;
  private participantFormRef!: HTMLFormElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * @standalone
   * The name of the participants custom availability.
   */
  @Prop() name: string = 'participant-custom-availability';

  /**
   * @standalone
   * The participants selected in the add participants section.
   */
  @Prop() participants: Participant[] = this.selectedConfiguration?.participants || [];

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * This event is fired when the participants custom availability changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The state to store the custom availability setting for participants.
   */
  @State() selectedParticipants: {
    [key: string]: {
      setCustom: boolean;
      tab: 'availability' | 'specific-time-availability';
      isOpen: boolean;
      openHours: OpenHours[];
      specificTimesAvailability: SpecificTimeAvailability[];
      name: string;
      availability?: ParticipantAvailability;
      timezone?: string;
      onlySpecificTimeAvailability?: boolean;
    };
  } = this.setParticipants(this.participants);

  /**
   * The validation error message for the component.
   */
  @State() validationError: string = '';

  // Watchers
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-participants-custom-availability', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('participants')
  participantsChangedHandler(newValue: Participant[], oldValue: Participant[]) {
    debug('nylas-participants-custom-availability', 'participantsChangedHandler', newValue, oldValue);
    if (!newValue || !oldValue || JSON.stringify(newValue) === JSON.stringify(oldValue)) {
      return;
    }
    this.selectedParticipants = this.setParticipants(newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration, oldValue: Configuration) {
    debug('nylas-participants-custom-availability', 'selectedConfigurationChangedHandler', newValue, oldValue);
    if (!newValue || !oldValue || JSON.stringify(newValue) === JSON.stringify(oldValue)) {
      return;
    }
    if (newValue?.participants) {
      this.selectedParticipants = this.setParticipants(newValue?.participants);
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  // Lifecycle Methods
  connectedCallback() {
    debug('nylas-participants-custom-availability', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-participants-custom-availability', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-participants-custom-availability', 'componentWillLoad');
  }

  componentDidLoad() {
    debug('nylas-participants-custom-availability', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedParticipants = this.setParticipants(this.selectedConfiguration?.participants);
    } else {
      this.selectedParticipants = this.setParticipants(this.participants);
    }
    // Run validation on initial load
    this.updateFormValue();
  }

  // Event Handlers
  @Listen('nylasFormSwitchToggled')
  nylasFormSwitchToggledHandler(event: CustomEvent<{ checked: boolean; name: string; label: boolean }>) {
    debug('nylas-participants-custom-availability', 'nylasFormSwitchToggledHandler', event.detail);

    // Check if this is a toggle for "only specific time availability"
    if (event.detail.name.startsWith('only-specific-time-')) {
      const participantEmail = event.detail.name.replace('only-specific-time-', '');
      if (this.selectedParticipants[participantEmail]) {
        this.selectedParticipants[participantEmail]['onlySpecificTimeAvailability'] = event.detail.checked;
        // When enabling only specific time, switch to specific time tab
        if (event.detail.checked) {
          this.selectedParticipants[participantEmail]['tab'] = 'specific-time-availability';
        }
        this.selectedParticipants = { ...this.selectedParticipants };
        this.updateFormValue();
      }
      return;
    }

    const existingParticipant = this.selectedConfiguration?.participants.find(participant => participant.email === event.detail.name);
    const existingOpenHours = existingParticipant?.availability?.open_hours || [];
    const openHours = existingOpenHours?.length > 0 ? existingOpenHours : this.selectedConfiguration?.availability?.availability_rules?.default_open_hours || [];
    const timeZone =
      existingOpenHours && existingOpenHours.length > 0
        ? existingParticipant?.timezone
        : this.selectedConfiguration?.availability?.availability_rules?.default_open_hours?.at(0)?.timezone ||
          this.selectedConfiguration?.event_booking?.timezone ||
          window.Intl.DateTimeFormat().resolvedOptions().timeZone;

    const existingSpecificTimeAvailability = existingParticipant?.specific_time_availability || [];
    const specificTimesAvailability = existingSpecificTimeAvailability?.length > 0 ? existingSpecificTimeAvailability : [];
    this.selectedParticipants[event.detail.name]['isOpen'] = event.detail.checked;
    this.selectedParticipants[event.detail.name]['tab'] = 'availability';
    this.selectedParticipants[event.detail.name]['setCustom'] = event.detail.checked;
    this.selectedParticipants[event.detail.name]['openHours'] = event.detail.checked ? (openHours?.length > 0 ? openHours : DEFAULT_OPEN_HOURS) : [];
    this.selectedParticipants[event.detail.name]['timezone'] = event.detail.checked ? timeZone : undefined;
    this.selectedParticipants[event.detail.name]['specificTimesAvailability'] = event.detail.checked ? specificTimesAvailability : [];
    // Reset onlySpecificTimeAvailability to false when custom availability is turned off
    this.selectedParticipants[event.detail.name]['onlySpecificTimeAvailability'] = event.detail.checked
      ? this.selectedParticipants[event.detail.name]['onlySpecificTimeAvailability'] || false
      : false;
    this.selectedParticipants = { ...this.selectedParticipants };
    this.updateFormValue();
  }

  @Listen('valueChanged')
  handleValueChanged(event: CustomEvent) {
    debug('[nylas-editor-tabs]', 'handleValueChanged', event);
    const { name, value } = event.detail;
    if (!name.startsWith('participant-availability-') && !name.startsWith('participant-specific-time-availability-')) {
      return;
    }

    // Parse the JSON value once - can contain { openHours: OpenHours[], timezone: string } or SpecificTimeAvailability[]
    let jsonValue;
    try {
      jsonValue = JSON.parse(value);
    } catch (err) {
      error(`Failed to parse JSON for participant ${name}:`, err);
      return;
    }

    // Validate the form
    if (this.isInternalsAvailable) {
      if (!this.participantFormRef.checkValidity()) {
        this.internals.setValidity({ customError: true }, 'Please fix the overlapping time ranges.');
        return;
      } else {
        this.internals.setValidity({});
      }
    }

    if (name.startsWith('participant-availability-')) {
      // Expected name format: "participant-availability-{participantId}-{uniqueKey}"
      // We extract the unique key (4th segment) to identify the specific participant
      const nameParts = name.split('-');
      if (nameParts.length < 4) {
        debug(`Malformed participant name format: ${name}. Expected format: "participant-availability-{participantId}-{uniqueKey}"`);
        return;
      }

      const key = nameParts[3];
      if (!this.selectedParticipants[key]) return;
      const existingOpenHours = jsonValue.openHours || [];
      const openHours = existingOpenHours?.length > 0 ? existingOpenHours : this.selectedConfiguration?.availability?.availability_rules?.default_open_hours || [];
      this.selectedParticipants[key]['openHours'] = openHours;
      this.selectedParticipants[key]['tab'] = 'availability';
      if (jsonValue.timezone) {
        this.selectedParticipants[key]['timezone'] = jsonValue.timezone;
        // Also sync to specific times availability so it is consistent
        this.selectedParticipants[key]['specificTimesAvailability'] = (this.selectedParticipants[key]['specificTimesAvailability'] || []).map(entry => ({
          ...entry,
          timezone: jsonValue.timezone,
        }));
      }
    } else if (name.startsWith('participant-specific-time-availability')) {
      // Expected name format: "participant-specific-time-availability-{index}-{uniqueKey}"
      // Extract the unique key (6th segment) to identify the specific participant
      const nameParts = name.split('-');
      if (nameParts.length < 6) {
        debug(`Malformed participant specific date name format: ${name}. Expected format: "participant-specific-time-availability-{index}-{uniqueKey}"`);
        return;
      }

      const key = nameParts[5];
      if (!this.selectedParticipants[key]) return;
      const existingSpecificTimeAvailability = jsonValue || [];
      this.selectedParticipants[key]['specificTimesAvailability'] = existingSpecificTimeAvailability;
      this.selectedParticipants[key]['tab'] = 'specific-time-availability';

      // Sync timezone from specific time availability
      if (existingSpecificTimeAvailability.length > 0 && existingSpecificTimeAvailability[0].timezone) {
        const newTimezone = existingSpecificTimeAvailability[0].timezone;
        this.selectedParticipants[key]['timezone'] = newTimezone;
        // Also sync to open hours so it is consistent
        this.selectedParticipants[key]['openHours'] = (this.selectedParticipants[key]['openHours'] || []).map(entry => ({
          ...entry,
          timezone: newTimezone,
        }));
      }
    }

    this.selectedParticipants = { ...this.selectedParticipants };

    this.updateFormValue();
  }

  updateFormValue() {
    const participantOpenHours = {};
    const participantSpecificTimeAvailability = {};
    const participantOnlySpecificTimeAvailability = {};
    Object.keys(this.selectedParticipants).forEach(key => {
      participantOpenHours[key] = this.selectedParticipants[key].openHours;
      participantSpecificTimeAvailability[key] = this.selectedParticipants[key].specificTimesAvailability || [];
      participantOnlySpecificTimeAvailability[key] = this.selectedParticipants[key].onlySpecificTimeAvailability || false;
    });

    // Validate that participants with "only specific time availability" have at least one specific time entry
    const invalidParticipants = Object.keys(this.selectedParticipants).filter(key => {
      const participant = this.selectedParticipants[key];
      return participant.onlySpecificTimeAvailability && (!participant.specificTimesAvailability || participant.specificTimesAvailability.length === 0);
    });

    if (invalidParticipants.length > 0) {
      const participantNames = invalidParticipants.map(key => this.selectedParticipants[key].name).join(', ');
      const errorMessage = i18next.t('nylasParticipantCustomAvailability.specificTimeRequired', { participantName: participantNames });
      this.validationError = errorMessage;
      if (this.isInternalsAvailable) {
        this.internals.setValidity({ customError: true }, errorMessage);
      }
    } else {
      this.validationError = '';
      if (this.isInternalsAvailable) {
        this.internals.setValidity({});
      }
    }

    this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(participantOpenHours), this.name);
    this.valueChanged.emit({ value: JSON.stringify(participantOpenHours), name: this.name });

    this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(participantSpecificTimeAvailability), `${this.name}-specific-time`);
    this.valueChanged.emit({ value: JSON.stringify(participantSpecificTimeAvailability), name: `${this.name}-specific-time` });

    this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(participantOnlySpecificTimeAvailability), `${this.name}-only-specific-time`);
    this.valueChanged.emit({ value: JSON.stringify(participantOnlySpecificTimeAvailability), name: `${this.name}-only-specific-time` });
  }

  setParticipants(participants: Participant[]) {
    const selectedParticipants = {};
    participants?.forEach(participant => {
      const onlySpecificTime = participant.only_specific_time_availability || false;
      const hasOpenHours = Boolean(participant.availability?.open_hours && participant.availability.open_hours.length > 0);
      const hasSpecificTime = Boolean(participant.specific_time_availability && participant.specific_time_availability.length > 0);
      const hasCustomAvailability = hasOpenHours || hasSpecificTime;

      selectedParticipants[participant.email] = {
        isOpen: hasCustomAvailability, // Open the section if custom availability is configured
        setCustom: hasOpenHours,
        openHours: participant.availability?.open_hours || [],
        name: participant.name || participant.email,
        availability: participant.availability,
        timezone: participant.timezone,
        specificTimesAvailability: participant.specific_time_availability || [],
        onlySpecificTimeAvailability: onlySpecificTime,
        // Default to specific-time-availability tab if only specific time is enabled
        tab: onlySpecificTime ? 'specific-time-availability' : 'availability',
      };
    });
    return selectedParticipants;
  }

  /**
   * This method is essentially a workaround to check if the internals are available because
   * the unit tests in stencil do not support the internals.
   * @returns boolean
   */
  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function' && typeof this.internals.setValidity === 'function';
  }

  @RegisterComponent<NylasParticipantsCustomAvailability, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-participants-custom-availability',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <div class="nylas-participants-custom-availability" part="npca">
          <div class="header" part="npca__header">
            <h3>{i18next.t('nylasParticipantCustomAvailability.headerTitle')}</h3>
            <p>{i18next.t('nylasParticipantCustomAvailability.headerSubTitle')}</p>
          </div>
          {this.validationError && (
            <div class="validation-error" part="npca__validation-error">
              <span>{this.validationError}</span>
            </div>
          )}
          <div class="content" part="npca__content">
            <form ref={el => (this.participantFormRef = el as HTMLFormElement)}>
              {Object.keys(this.selectedParticipants).map((key, index) => {
                const participant = this.selectedParticipants[key];
                const participantOpenHours = participant.availability?.open_hours || [];
                if (!participant || !participant.name) return;
                return (
                  <div class="participant-container" part="npca__participant-container" key={`participant-conatiner-${index}`}>
                    <div class="participant-title" part="npca__participant-title">
                      <p>{i18next.t('nylasParticipantCustomAvailability.participantOpenHoursTitle', { participantName: participant.name })}</p>
                      <div class="participant-toggle" part="npca__participant-toggle--container">
                        <toggle-switch
                          exportparts="ts_label: npca__toggle-label, ts_input: npca_toggle-input, ts_slider: npca_toggle-slider"
                          name={key}
                          checked={!!participant?.openHours && participant?.openHours.length > 0}
                          themeConfig={this.themeConfig}
                        />
                        <span
                          class={`chevron ${participant.isOpen ? 'open' : 'closed'} ${participant.setCustom ? '' : 'disabled'}`}
                          onClick={() => {
                            if (!participant.setCustom) return;
                            this.selectedParticipants[key].isOpen = !participant.isOpen;
                            this.selectedParticipants[key].tab = 'availability';
                            this.selectedParticipants = { ...this.selectedParticipants };
                          }}
                        >
                          <chevron-icon width="24" height="24" />
                        </span>
                      </div>
                    </div>
                    {participant.isOpen && (
                      <div class="participant-only-specific-time-container">
                        <div class="participant-only-specific-time-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={participant.onlySpecificTimeAvailability}
                              onChange={(e: any) => {
                                this.selectedParticipants[key]['onlySpecificTimeAvailability'] = e.target.checked;
                                if (e.target.checked) {
                                  this.selectedParticipants[key]['tab'] = 'specific-time-availability';
                                }
                                this.selectedParticipants = { ...this.selectedParticipants };
                                this.updateFormValue();
                              }}
                            />
                            <span>{i18next.t('nylasParticipantCustomAvailability.onlySpecificTimeAvailability', { participantName: participant.name })}</span>
                            <tooltip-component>
                              <info-icon slot="tooltip-icon" width="14" height="14" />
                              <span slot="tooltip-content">
                                {i18next.t('nylasParticipantCustomAvailability.onlySpecificTimeAvailabilityTooltip', { participantName: participant.name })}
                              </span>
                            </tooltip-component>
                          </label>
                        </div>
                      </div>
                    )}
                    {participant.isOpen && (
                      <div class={`participant-tabs ${participant.onlySpecificTimeAvailability ? 'single-tab' : ''}`} part="npca__participant-tabs">
                        {!participant.onlySpecificTimeAvailability && (
                          <div
                            class={`participant-tab ${participant.tab === 'availability' ? 'active' : ''}`}
                            part="npca__participant-tab"
                            onClick={() => {
                              this.selectedParticipants[key].tab = 'availability';
                              this.selectedParticipants = { ...this.selectedParticipants };
                            }}
                          >
                            <span>{i18next.t('nylasParticipantCustomAvailability.participantAvailabilityTab')}</span>
                          </div>
                        )}
                        <div
                          class={`participant-tab ${participant.tab === 'specific-time-availability' ? 'active' : ''}`}
                          part="npca__participant-tab"
                          onClick={() => {
                            this.selectedParticipants[key].tab = 'specific-time-availability';
                            this.selectedParticipants = { ...this.selectedParticipants };
                          }}
                        >
                          <span>{i18next.t('nylasParticipantCustomAvailability.participantSpecificTimeAvailabilityTab')}</span>
                        </div>
                      </div>
                    )}
                    {participant.tab === 'availability' && participant.isOpen && !participant.onlySpecificTimeAvailability && (
                      <nylas-availability-picker
                        key={index}
                        name={`participant-availability-${index}-${key}`}
                        openHours={participantOpenHours}
                        defaultTimezone={participant.timezone}
                        hideHeader={true}
                      />
                    )}
                    {participant.tab === 'specific-time-availability' && participant.isOpen && (
                      <nylas-specific-time-availability-picker
                        key={index}
                        name={`participant-specific-time-availability-${index}-${key}`}
                        defaultTimezone={participant.timezone}
                        specificTimesAvailability={participant.specificTimesAvailability}
                        hideHeader={true}
                      />
                    )}
                  </div>
                );
              })}
            </form>
          </div>
        </div>
      </Host>
    );
  }
}
